import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import { ArticleCard } from "@/components/feature/ArticleCard";
import { articles, categories, aboutFaqs, site } from "@/mocks/siteData";

const heroImg =
  "https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20dim%20ancient%20stone%20chamber%20lit%20by%20warm%20flickering%20candlelight%2C%20weathered%20bronze%20ritual%20vessels%20and%20scattered%20bamboo%20slips%20on%20a%20carved%20stone%20altar%2C%20dust%20motes%20in%20golden%20light%20beam%2C%20deep%20shadows%2C%20burnt%20umber%20and%20amber%20palette%2C%20painterly%20editorial%20style%2C%20high%20detail&width=1600&height=900&seq=lieqi-hero&orientation=landscape";

const valuesImg =
  "https://readdy.ai/api/search-image?query=stylized%20still%20life%20of%20ancient%20archaeology%20field%20notes%2C%20calipers%2C%20ink%20drawings%20of%20pottery%20and%20a%20magnifying%20glass%20on%20warm%20aged%20paper%2C%20muted%20amber%20and%20ochre%20tones%2C%20scholarly%20editorial%20photography%2C%20high%20detail%2C%20warm%20light&width=1400&height=900&seq=lieqi-values&orientation=landscape";

function FeaturedStrip() {
  const big = articles.find((a) => a.slug === "youshen-ershi") ?? articles[0];
  const small = articles.filter((a) => a.slug !== big.slug).slice(0, 3);
  return (
    <section aria-label="本周深度" className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="mb-2 inline-flex items-center gap-2 rounded-full bg-accent-500/10 px-3 py-1 text-xs font-semibold tracking-wide text-accent-700">
            <i className="ri-fire-line" aria-hidden="true"></i>
            本周深度
          </p>
          <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-4xl">
            精选探访，一层层往下挖
          </h2>
        </div>
        <Link
          to={`/article/${big.slug}`}
          className="hidden shrink-0 items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-accent-600 md:inline-flex"
        >
          进入现场 <i className="ri-arrow-right-line" aria-hidden="true"></i>
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <article className="group relative overflow-hidden rounded-xl border border-background-200 bg-foreground-950 text-background-50">
          <Link to={`/article/${big.slug}`} className="relative block">
            <img
              src={big.cover}
              alt={`${big.title} · 古墓探秘现场`}
              className="h-72 w-full object-cover object-top opacity-80 transition-opacity group-hover:opacity-95 md:h-[420px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/30 to-transparent"></div>
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                <i className="ri-vip-crown-2-line" aria-hidden="true"></i>
                长文深度稿
              </span>
              <h3 className="mt-3 font-heading text-xl font-bold leading-snug md:text-2xl">
                {big.title}
              </h3>
              <p className="mt-2 line-clamp-2 max-w-xl text-sm text-background-100/75">
                {big.excerpt}
              </p>
              <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary-300">
                阅读全文 <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </span>
            </div>
          </Link>
        </article>

        <div className="flex flex-col divide-y divide-background-200">
          {small.map((a) => (
            <Link
              key={a.id}
              to={`/article/${a.slug}`}
              className="group flex gap-4 py-5"
            >
              <div className="h-20 w-28 shrink-0 overflow-hidden rounded-lg border border-background-200 md:h-24 md:w-36">
                <img
                  src={a.cover}
                  alt={`${a.title} 配图`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="min-w-0">
                <h3 className="font-heading text-base font-bold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700 md:text-lg">
                  {a.title}
                </h3>
                <p className="mt-1.5 line-clamp-2 text-sm text-foreground-600">{a.excerpt}</p>
                <span className="mt-2 inline-flex items-center gap-3 text-xs text-foreground-500">
                  <span>{a.author}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    {a.readTime}
                  </span>
                </span>
              </div>
            </Link>
          ))}
          <div className="pt-4">
            <Link
              to={`/article/${big.slug}`}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 transition-colors hover:text-accent-600 md:hidden"
            >
              进入现场 <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function CategoryGrid() {
  return (
    <section aria-label="主题栏目" className="bg-background-100">
      <div className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-primary-700">EXPLORE TOPICS</p>
          <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-4xl">
            顺着哪条线索，走进地下世界
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            四大主题方向，从史书记载到考古现场，从墓室深处到传说坐标，任选一条路开始。
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/explore/${c.slug}`}
              className="group relative overflow-hidden rounded-xl border border-background-200 bg-foreground-950"
            >
              <img
                src={c.cover}
                alt={`${c.name}栏目 ${c.en}`}
                className="h-56 w-full object-cover object-top opacity-85 transition-transform duration-500 group-hover:scale-105 md:h-64"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/20 to-transparent"></div>
              <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
                <p className="text-[11px] font-medium tracking-[0.18em] text-primary-300">{c.en}</p>
                <h3 className="mt-1 font-heading text-lg font-bold text-background-50 md:text-xl">
                  {c.name}
                </h3>
                <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-background-100/70">
                  {c.tagline}
                </p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-primary-300">
                  查看栏目
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestGrid() {
  const latest = useMemo(
    () => [...articles].sort((x, y) => (x.date < y.date ? 1 : -1)).slice(0, 4),
    []
  );
  return (
    <section aria-label="最新线索" className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
      <div className="mb-8 flex items-end justify-between gap-4">
        <div>
          <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-primary-700">LATEST LEADS</p>
          <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-4xl">最新线索速递</h2>
        </div>
        <Link
          to="/explore/lishi-miwen"
          className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-accent-600"
        >
          查看全部 <i className="ri-arrow-right-line" aria-hidden="true"></i>
        </Link>
      </div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {latest.map((a) => (
          <ArticleCard key={a.id} article={a} />
        ))}
      </div>
    </section>
  );
}

function EditorialBand() {
  return (
    <section aria-label="我们如何做历史" className="bg-foreground-950 text-background-100">
      <div className="mx-auto grid max-w-[1400px] items-center gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-2">
        <div className="relative">
          <img
            src={valuesImg}
            alt="猎奇网的田野笔记与考据工具"
            className="aspect-[4/3] w-full rounded-xl border border-background-50/10 object-cover object-top"
          />
          <div className="absolute -bottom-5 -right-3 hidden items-center gap-3 rounded-lg bg-accent-500 px-4 py-3 text-background-50 sm:flex">
            <i className="ri-focus-3-line text-2xl" aria-hidden="true"></i>
            <div className="leading-tight">
              <p className="font-heading text-xl font-bold">史料 + 实地</p>
              <p className="text-xs text-background-50/80">让证据自己开口</p>
            </div>
          </div>
        </div>
        <div>
          <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-primary-300">OUR METHOD</p>
          <h2 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">
            我们不猜谜底，我们追踪证据
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-background-100/75 md:text-base">
            {site.intro}
          </p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[
              { icon: "ri-file-history-line", t: "考据为本", d: "回到一手文献与发掘简报" },
              { icon: "ri-footprint-line", t: "实地求证", d: "线索落地，用物证校准传说" },
              { icon: "ri-question-line", t: "保留争议", d: "多说法并列，不替历史下结论" },
            ].map((f) => (
              <div key={f.t} className="rounded-lg border border-background-50/10 bg-background-50/5 p-4">
                <i className={`${f.icon} text-2xl text-primary-400`} aria-hidden="true"></i>
                <h3 className="mt-2 font-heading text-base font-bold text-background-50">{f.t}</h3>
                <p className="mt-1 text-xs leading-relaxed text-background-100/65">{f.d}</p>
              </div>
            ))}
          </div>
          <Link
            to="/about"
            className="mt-7 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            认识我们 <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section aria-label="常见问题" className="mx-auto max-w-[1400px] px-4 py-14 md:px-6 md:py-20">
      <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr]">
        <div>
          <p className="mb-2 text-xs font-semibold tracking-[0.2em] text-primary-700">COMMON QUESTIONS</p>
          <h2 className="font-heading text-2xl font-bold text-foreground-900 md:text-4xl">
            关于猎奇，你可能想问
          </h2>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            我们如何判断史料真伪、如何看待传说不确定性，以及怎样跟进新选题，都在这里。
          </p>
        </div>
        <div className="divide-y divide-background-200 rounded-xl border border-background-200 bg-background-50">
          {aboutFaqs.map((item, i) => (
            <div key={i}>
              <button
                type="button"
                onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                aria-expanded={openIdx === i}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-6"
              >
                <h3 className="font-heading text-base font-bold text-foreground-900 md:text-lg">
                  {item.q}
                </h3>
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
                    openIdx === i ? "bg-primary-500 text-background-50" : "bg-secondary-100 text-secondary-700"
                  }`}
                >
                  <i
                    className={`transition-transform ${openIdx === i ? "ri-subtract-line" : "ri-add-line"}`}
                    aria-hidden="true"
                  ></i>
                </span>
              </button>
              {openIdx === i && (
                <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600 md:px-6">
                  {item.a}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-foreground-950 text-background-50">
      <img
        src={heroImg}
        alt="猎奇网 · 幽暗烛光下的青铜古器与竹简"
        className="absolute inset-0 h-full w-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-foreground-950/45 to-foreground-950"></div>
      <div className="relative mx-auto flex min-h-[92vh] w-full max-w-[1400px] flex-col items-center justify-center px-4 py-28 text-center md:px-6">
        <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-background-50/20 bg-black/25 px-4 py-1.5 text-xs tracking-wide text-background-100/85 backdrop-blur">
          <span className="h-1.5 w-1.5 rounded-full bg-accent-500" aria-hidden="true"></span>
          历史秘闻 · 考古发现 · 古墓探秘 · 谜团追踪
        </p>
        <h1 className="max-w-4xl text-balance font-heading text-4xl font-black leading-tight md:text-6xl">
          掀开历史的封土
          <br className="md:hidden" />
          <span className="mt-2 inline-block text-primary-300 md:mt-0">聆听地下的回响</span>
        </h1>
        <p className="mt-6 max-w-2xl text-balance text-sm leading-relaxed text-background-100/85 md:text-lg">
          从正史边角、墓室深处与民间旧谈出发，深挖{site.name}历史秘闻与考古发现，
          结合史料与实地证据，还原那些被时间掩埋的古代奇事。
        </p>
        <div className="mt-9 flex flex-col items-center gap-3 sm:flex-row">
          <Link
            to="/explore/lishi-miwen"
            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
          >
            开始探秘 <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
          <a
            href="#topics"
            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/25 bg-black/15 px-7 py-3.5 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-black/30 sm:w-auto"
          >
            浏览栏目
          </a>
        </div>

        <dl className="mt-14 grid w-full max-w-2xl grid-cols-3 gap-6 border-t border-background-50/15 pt-8">
          {[
            { n: "4", t: "大主题方向" },
            { n: "12", t: "篇深度探访稿" },
            { n: "每周", t: "持续更新新线索" },
          ].map((s) => (
            <div key={s.t}>
              <dd className="font-heading text-2xl font-black text-primary-300 md:text-4xl">{s.n}</dd>
              <dt className="mt-1 text-xs text-background-100/70">{s.t}</dt>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: aboutFaqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Home() {
  useEffect(() => {
    document.title = "猎奇网 - 历史秘闻 考古发现 古墓探秘 历史谜团追踪";
  }, []);
  return (
    <div className="bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
      />
      <SiteNav />
      <main>
        <Hero />
        <div id="topics"></div>
        <FeaturedStrip />
        <CategoryGrid />
        <LatestGrid />
        <EditorialBand />
        <FaqSection />
      </main>
      <SiteFooter />
    </div>
  );
}